import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { QrCode, CheckCircle2, AlertTriangle, RotateCw, Camera, KeyRound } from "lucide-react";

const READER_ID = "qr-reader";

export default function Scanner() {
  const [running, setRunning] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [lastResult, setLastResult] = useState(null); // { ok, already, ticket, code, error }
  const [checkingIn, setCheckingIn] = useState(false);
  const html5Ref = useRef(null);
  const lastCodeRef = useRef({ code: null, at: 0 });

  useEffect(() => {
    return () => {
      // cleanup on unmount
      const inst = html5Ref.current;
      if (inst) {
        inst.stop().catch(() => {}).finally(() => inst.clear());
        html5Ref.current = null;
      }
    };
  }, []);

  const checkin = async (code) => {
    if (!code) return;
    // Debounce: same code within 3s → ignore
    const now = Date.now();
    if (lastCodeRef.current.code === code && now - lastCodeRef.current.at < 3000) return;
    lastCodeRef.current = { code, at: now };
    setCheckingIn(true);
    try {
      const { data } = await http.post(`/tickets/${encodeURIComponent(code)}/checkin`);
      const result = { ok: true, already: !!data.already, ticket: data.ticket, code };
      setLastResult(result);
      if (data.already) {
        toast.warning(`Already checked in · ${data.ticket?.buyer_name || code}`);
      } else {
        toast.success(`Checked in · ${data.ticket?.buyer_name || code}`);
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Ticket not found";
      setLastResult({ ok: false, error: msg, code });
      toast.error(msg);
    } finally {
      setCheckingIn(false);
    }
  };

  const start = async () => {
    if (running) return;
    try {
      const inst = new Html5Qrcode(READER_ID, { verbose: false });
      html5Ref.current = inst;
      await inst.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decodedText) => checkin(decodedText.trim()),
        () => {}, // ignore per-frame decode errors
      );
      setRunning(true);
    } catch (e) {
      toast.error("Could not open camera. Grant camera access, or use the manual entry field below.");
      console.error(e);
    }
  };

  const stop = async () => {
    const inst = html5Ref.current;
    if (!inst) return setRunning(false);
    try { await inst.stop(); } catch {}
    try { inst.clear(); } catch {}
    html5Ref.current = null;
    setRunning(false);
  };

  const submitManual = (e) => {
    e.preventDefault();
    const code = manualCode.trim();
    if (!code) return;
    checkin(code);
    setManualCode("");
  };

  return (
    <div className="p-8 lg:p-10 max-w-4xl">
      <div className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Scanner</div>
      <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl tracking-tight">Ticket check-in</h1>
      <p className="mt-2 text-sm text-zinc-500 max-w-2xl">
        Point your camera at a buyer&apos;s ticket QR code. Codes check in instantly. You&apos;ll be warned if a ticket has already been used.
      </p>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        {/* Camera panel */}
        <div className="rounded-xl bg-white border border-zinc-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 font-semibold">
              <QrCode className="w-4 h-4" /> Camera
            </div>
            {running ? (
              <Button variant="outline" size="sm" onClick={stop} className="rounded-full" data-testid="scanner-stop">
                <RotateCw className="w-3.5 h-3.5 mr-1" /> Stop
              </Button>
            ) : (
              <Button size="sm" onClick={start} className="rounded-full bg-zinc-900 hover:bg-zinc-800" data-testid="scanner-start">
                <Camera className="w-3.5 h-3.5 mr-1" /> Start scanning
              </Button>
            )}
          </div>
          <div
            id={READER_ID}
            data-testid="scanner-video"
            className="rounded-lg overflow-hidden bg-zinc-100 aspect-square grid place-items-center text-sm text-zinc-500"
          >
            {!running && (
              <div className="text-center px-6">
                <Camera className="w-10 h-10 mx-auto text-zinc-300" />
                <div className="mt-3 text-zinc-500">Tap &quot;Start scanning&quot; and allow camera access.</div>
              </div>
            )}
          </div>
        </div>

        {/* Manual entry + last result */}
        <div className="space-y-6">
          <div className="rounded-xl bg-white border border-zinc-200 p-6">
            <div className="flex items-center gap-2 font-semibold mb-3">
              <KeyRound className="w-4 h-4" /> Manual code entry
            </div>
            <form onSubmit={submitManual} className="flex gap-2">
              <Input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="TKT-XXXXXXXXXX"
                data-testid="scanner-manual-input"
                className="h-11 font-mono"
              />
              <Button type="submit" disabled={checkingIn || !manualCode.trim()} data-testid="scanner-manual-submit" className="rounded-full bg-zinc-900 hover:bg-zinc-800">
                Check in
              </Button>
            </form>
            <div className="mt-2 text-xs text-zinc-500">Type or paste the ticket code shown under the QR image.</div>
          </div>

          {/* Result panel */}
          <div className="rounded-xl bg-white border border-zinc-200 p-6 min-h-[180px]" data-testid="scanner-last-result">
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Last scan</div>
            {!lastResult && (
              <div className="mt-3 text-sm text-zinc-500">No tickets scanned yet.</div>
            )}
            {lastResult && lastResult.ok && !lastResult.already && (
              <div className="mt-3 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full grid place-items-center bg-emerald-100 text-emerald-700 shrink-0">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <Badge className="bg-emerald-600">Checked in</Badge>
                  <div className="mt-2 font-semibold">{lastResult.ticket?.buyer_name || "Guest"}</div>
                  <div className="text-sm text-zinc-500">{lastResult.ticket?.tier_name}</div>
                  <div className="mt-1 text-xs font-mono text-zinc-400">{lastResult.code}</div>
                </div>
              </div>
            )}
            {lastResult && lastResult.ok && lastResult.already && (
              <div className="mt-3 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full grid place-items-center bg-amber-100 text-amber-700 shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <Badge variant="outline" className="border-amber-400 text-amber-700">Already used</Badge>
                  <div className="mt-2 font-semibold">{lastResult.ticket?.buyer_name || "Guest"}</div>
                  <div className="text-sm text-zinc-500">{lastResult.ticket?.tier_name}</div>
                  <div className="mt-1 text-xs font-mono text-zinc-400">{lastResult.code}</div>
                </div>
              </div>
            )}
            {lastResult && !lastResult.ok && (
              <div className="mt-3 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full grid place-items-center bg-red-100 text-red-700 shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <Badge className="bg-red-600">Invalid</Badge>
                  <div className="mt-2 text-sm text-red-700">{lastResult.error}</div>
                  <div className="mt-1 text-xs font-mono text-zinc-400">{lastResult.code}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
